// Path: frontend/src/contexts/SnackbarContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  memo,
} from "react";
import { Snackbar, Alert } from "@mui/material";

const SnackbarContext = createContext({});

// Memoize the Snackbar component to prevent unnecessary re-renders
const MemoizedSnackbar = memo(({ open, message, severity, onClose }) => (
  <Snackbar
    open={open}
    autoHideDuration={6000}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    sx={{ position: "fixed" }} // Ensure it stays fixed
  >
    <Alert
      onClose={onClose}
      severity={severity}
      sx={{ width: "100%" }}
      elevation={6}
    >
      {message}
    </Alert>
  </Snackbar>
));

export const SnackbarProvider = ({ children }) => {
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = useCallback((message, severity = "info") => {
    setSnackbarState({
      open: true,
      message,
      severity,
    });
  }, []);

  const hideSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarState((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const value = React.useMemo(
    () => ({
      showSnackbar,
    }),
    [showSnackbar]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <MemoizedSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        severity={snackbarState.severity}
        onClose={hideSnackbar}
      />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};
